import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, CircularProgress, Button, LinearProgress, Grid } from '@mui/material';
import { getAssessmentResult } from 'framework/employeeWorkshopAssessment';
import { useAlert } from 'contexts/alertContext';
import { handleError } from 'helpers/utils';
import { useTranslation } from 'react-i18next';

interface CategoryResult {
  key: string;
  titleEn: string;
  titleAr: string;
  score: number;
  rawScore: number;
  maxScore: number;
  order: number;
  resultEn?: string;  // Optional text result for assessments like Mindset
  resultAr?: string;  // Optional text result for assessments like Mindset
}

interface AssessmentResult {
  assessmentType: string;
  assessmentTitle: string;
  assessmentTitleAr: string;
  summary: string;
  isarabic: boolean;
  categories: CategoryResult[];
  employeeId: string;
  userId: string;
}

const EmployeeWorkshopAssessmentResult: React.FC = () => {
  const { assessmentDefinitionId } = useParams<{ assessmentDefinitionId: string }>();
  const navigate = useNavigate();
  const { setAlert } = useAlert();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [result, setResult] = useState<AssessmentResult | null>(null);

  useEffect(() => {
    const fetchResult = async () => {
      if (!assessmentDefinitionId) {
        setAlert({
          open: true,
          message: 'Assessment ID is required',
          type: 'error',
        });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getAssessmentResult(assessmentDefinitionId);
        setResult(data);
        setLoading(false);
      } catch (error: any) {
        setAlert({
          open: true,
          message: handleError(error) || 'Failed to load assessment result',
          type: 'error',
        });
        setLoading(false);
      }
    };

    fetchResult();
  }, [assessmentDefinitionId, setAlert]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!result) {
    return (
      <Box p={3}>
        <Typography variant="h5" gutterBottom>
          No result found
        </Typography>
        <Button variant="contained" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  // Helper function to get color based on score
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success.main';
    if (score >= 60) return 'info.main';
    if (score >= 40) return 'warning.main';
    return 'error.main';
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Paper elevation={3} sx={{ padding: 4, maxWidth: 1000, margin: '0 auto' }}>
        <Typography variant="h4" gutterBottom align="center" sx={{ mb: 1 }}>
          {result.isarabic ? result.assessmentTitleAr : result.assessmentTitle}
        </Typography>
        
        <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          {t('T_assessment_result') || 'Assessment Result'}
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          {t('T_detailed_scores') || 'Detailed Scores'}
        </Typography>

        <Grid container spacing={3}>
          {result.categories && result.categories.length > 0 ? (
            result.categories
              .sort((a, b) => a.order - b.order)
              .map((category) => {
                return (
                  <Grid item xs={12} key={category.key}>
                    <Paper 
                      elevation={2} 
                      sx={{ 
                        p: 3, 
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 4
                        }
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" fontWeight={600}>
                          {result.isarabic ? category.titleAr : category.titleEn}
                        </Typography>
                        <Box textAlign="right">
                          <Typography variant="h4" color={getScoreColor(category.score)} fontWeight="bold">
                            {category.score.toFixed(0)}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {category.rawScore.toFixed(1)} / {category.maxScore}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Display text result if available (for assessments like Mindset) */}
                      {(category.resultEn || category.resultAr) && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                          <Typography variant="h6" align="center" color="primary.main" fontWeight="bold">
                            {result.isarabic ? category.resultAr : category.resultEn}
                          </Typography>
                        </Box>
                      )}
                      
                      <LinearProgress 
                        variant="determinate" 
                        value={category.score} 
                        sx={{ 
                          height: 10, 
                          borderRadius: 5,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getScoreColor(category.score),
                            borderRadius: 5
                          }
                        }} 
                      />
                    </Paper>
                  </Grid>
                );
              })
          ) : (
            <Grid item xs={12}>
              <Typography variant="body1" color="text.secondary" align="center">
                {t('T_no_categories_available') || 'No category data available'}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            {t('T_back') || 'Back'}
          </Button>
          <Button variant="contained" onClick={() => window.print()}>
            {t('T_print') || 'Print Result'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default EmployeeWorkshopAssessmentResult;

